import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

import {
  Link,
  Loader,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import {
  Column,
  ColumnOptions,
  ColumnType,
  Table,
  UserCell,
} from "@transfr-inc/dashboard-components/data-table";

import { ExemptUserModal } from "@components/modals";
import { MasteryCell } from "@components/table-cells";
import { useNotificationContext } from "@contexts/notification-context";
import { createUserDetailLink } from "@lib/utils/navigation.utils";
import container from "@src/container";

const getGradebookColumns = (simulations) => {
  return simulations.map(
    (sim) =>
      new Column(
        sim.learningObjectiveId,
        sim.displayName,
        ColumnType.CUSTOM,
        new ColumnOptions({
          customFunction: (value) => {
            // A cell can be momentarily undefined while columns (the selected
            // module's sims) and row data re-derive on a filter change.
            return (
              <MasteryCell
                status={value?.status}
                score={value?.mastery}
                exempt={value?.exempt}
                scoreOverrideAt={value?.scoreOverrideAt}
              />
            );
          },
        })
      )
  );
};

export const ClassroomGradebookTable = ({
  data,
  simulations,
  classroom,
  maxVisibleSims = 7,
  onSortClick,
  isLoading,
  readOnly = false,
  onScoreUpdated,
}) => {
  const [gradebookColumns, setGradebookColumns] = useState();
  const [tableData, setTableData] = useState();
  const [exemptUserModalOpen, setExemptUserModalOpen] = useState(false);
  const [exemptUserData, setExemptUserData] = useState();

  const { insightsService } = container;
  const { addNotification } = useNotificationContext();

  const UsersColumn = new Column(
    "lastName",
    "Name",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      isFixed: true,
      width: 250,
      sortKeyFields: ["isAnonymous", "lastName"],
      customFunction: (_, row) => {
        return (
          <UserCell
            lastName={row.lastName}
            firstName={row.firstName}
            isAnonymous={row.isAnonymous}
            userDetailRoute={
              <Link
                routerComponent={NavLink}
                to={createUserDetailLink({
                  userId: row.userId,
                  search: {
                    classroomId: classroom.classroomId,
                    from: classroom.name,
                  },
                })}
              >{`${row.lastName}, ${row.firstName}`}</Link>
            }
          />
        );
      },
    })
  );

  useEffect(() => {
    if (data && simulations) {
      const cols = getGradebookColumns(simulations);
      setGradebookColumns([UsersColumn, ...cols]);
    }
  }, [data, simulations]);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const handleCellClick = (cellData) => {
    if (!cellData?.userId || !cellData?.learningExperienceId) return;
    setExemptUserData(cellData);
    setExemptUserModalOpen(true);
  };

  const updateTraineeMetric = async (data) => {
    const {
      userId,
      learningExperienceId,
      simulationCode,
      exempt,
      exemptReason,
      masteryScore,
    } = data;

    try {
      await insightsService.updateUserActivityMetrics(
        classroom.courseId,
        classroom.classroomId,
        userId,
        {
          learningExperienceId,
          simulationCode,
          exempt,
          exemptReason,
          contentType: "inHeadset",
          masteryScore,
        }
      );

      // Refetch so the gradebook shows fresh server data (and the active
      // filters re-apply against it) instead of an optimistic guess.
      await onScoreUpdated?.();

      addNotification({
        type: NotificationType.success,
        message: "Score updated.",
        closable: true,
      });
    } catch (error) {
      console.error("Error updating trainee metric:", error);
      addNotification({
        type: NotificationType.error,
        message: "Error updating score. Please try again.",
        closable: true,
      });
    } finally {
      setExemptUserModalOpen();
    }
  };

  return (
    <>
      <ExemptUserModal
        open={exemptUserModalOpen}
        onClose={() => setExemptUserModalOpen()}
        onApply={updateTraineeMetric}
        initialData={exemptUserData}
        // A finished sim can be overridden, and an already-overridden one
        // stays editable: an override below 90 rewrites the status to
        // "In progress", which clears `completed`.
        allowScoreEdit={
          !!exemptUserData?.completed ||
          exemptUserData?.simCompletionCount > 0 ||
          !!exemptUserData?.scoreOverrideAt
        }
      />
      {isLoading && <Loader fixed />}
      {tableData && gradebookColumns && (
        <Table
          className="gradebook-table"
          columns={gradebookColumns}
          data={tableData}
          maxVisibleColumns={maxVisibleSims}
          onSortClick={onSortClick}
          staticHeaders
          keyValue="userId"
          minVisibleRows={10}
          handleCellClick={readOnly ? undefined : handleCellClick}
        ></Table>
      )}
    </>
  );
};
